/**
 * WebSocket service for real-time updates from Solana Tracker API
 * Handles subscriptions to token transactions, prices, market data, and other real-time data
 * 
 * Based on https://docs.solanatracker.io/public-data-api/websocket
 */

import { Keypair } from '@solana/web3.js';
import * as bs58 from 'bs58';
import CryptoJS from 'crypto-js';


// Get the application salt from environment variables
const APP_SALT = import.meta.env.VITE_APP_SALT;

class WebSocketService {
    private socket: WebSocket | null = null;
    private connectionStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
    private reconnectTimeout: NodeJS.Timeout | null = null;
    private heartbeatInterval: NodeJS.Timeout | null = null;
    private reconnectAttempts = 0;
    private readonly maxReconnectAttempts = 30;
    private readonly reconnectInterval = 2000;
    private readonly heartbeatIntervalTime = 30000;
    private eventListeners: Map<string, Set<(data: any) => void>> = new Map();
    private joinedRooms: Set<string> = new Set();
    private readonly wsUrl = import.meta.env.VITE_SOLANA_TRACKER_WS_URL || 'wss://ws.solanatracker.io';
    private readonly apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY || '';

    // Store active keypairs for trading wallets
    private activeKeypairs: Map<string, Keypair> = new Map();

    // Add method to set active keypair
    setActiveKeypair(tradingWalletAddress: string, keypair: Keypair): void {
        this.activeKeypairs.set(tradingWalletAddress, keypair);
    }

    // Add method to get active keypair
    getActiveKeypair(tradingWalletAddress: string): Keypair | null {
        return this.activeKeypairs.get(tradingWalletAddress) || null;
    }

    // Add method to remove active keypair
    removeActiveKeypair(tradingWalletAddress: string): void {
        const hadKeypair = this.activeKeypairs.has(tradingWalletAddress);
        this.activeKeypairs.delete(tradingWalletAddress);

        // If we've removed a keypair and have no more active keypairs,
        // trigger a reconnection event to ensure charts and data services recover
        if (hadKeypair && this.activeKeypairs.size === 0) {
            // Notify listeners of a configuration change
            if (this.eventListeners.has('reconnected')) {
                const listeners = this.eventListeners.get('reconnected');
                if (listeners) {
                    listeners.forEach(callback => {
                        try {
                            callback({
                                status: 'configuration_changed',
                                time: Date.now(),
                                keypairRemoved: tradingWalletAddress
                            });
                        } catch (error) {
                            console.error('Error in reconfiguration callback', error);
                        }
                    });
                }
            }
        }
    }

    /**
     * Initialize WebSocket connection
     */
    connect(): Promise<boolean> {
        if (this.connectionStatus === 'connected') {
            return Promise.resolve(true);
        }

        if (this.connectionStatus === 'connecting') {
            return new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (this.connectionStatus === 'connected') {
                        clearInterval(checkInterval);
                        resolve(true);
                    } else if (this.connectionStatus === 'disconnected') {
                        clearInterval(checkInterval);
                        resolve(false);
                    }
                }, 100);
            });
        }

        this.connectionStatus = 'connecting';

        return new Promise((resolve) => {
            try {
                console.log(`🔌 Connecting to WebSocket: ${this.wsUrl}`);
                console.log(`🔑 Using API key: ${this.apiKey ? 'Yes' : 'No'}`);

                // Initialize new WebSocket connection with the API key as a query parameter if available
                const url = this.apiKey ? `${this.wsUrl}?api_key=${this.apiKey}` : this.wsUrl;
                this.socket = new WebSocket(url);

                this.socket.onopen = () => {
                    const wasReconnection = this.reconnectAttempts > 0;
                    this.connectionStatus = 'connected';
                    this.reconnectAttempts = 0;

                    // Send authentication message with API key if available
                    if (this.apiKey && this.socket) {
                        this.socket.send(JSON.stringify({
                            type: 'auth',
                            apiKey: this.apiKey
                        }));
                    }

                    // Start heartbeat to keep connection alive
                    this.startHeartbeat();

                    // Rejoin previously joined rooms
                    this.joinedRooms.forEach(roomId => {
                        this.sendJoinRoomMessage(roomId);
                    });

                    // If this was a reconnection, notify listeners
                    if (wasReconnection) {
                        if (this.eventListeners.has('reconnected')) {
                            const listeners = this.eventListeners.get('reconnected');
                            if (listeners) {
                                listeners.forEach(callback => {
                                    try {
                                        callback({ status: 'reconnected', time: Date.now() });
                                    } catch (error) {
                                        console.error('Error in reconnection callback', error);
                                    }
                                });
                            }
                        }
                    }

                    resolve(true);
                };

                this.socket.onclose = (event) => {
                    this.connectionStatus = 'disconnected';
                    this.socket = null;

                    // Clear heartbeat
                    if (this.heartbeatInterval) {
                        clearInterval(this.heartbeatInterval);
                        this.heartbeatInterval = null;
                    }

                    // Attempt to reconnect
                    this.attemptReconnect();
                };

                this.socket.onerror = (error) => {
                    console.error('WebSocket connection error:', error);
                    this.connectionStatus = 'disconnected';
                    this.socket = null;

                    resolve(false);
                };

                this.socket.onmessage = (event) => {
                    this.handleMessage(event);
                };
            } catch (error) {
                console.error('Error creating WebSocket:', error);
                this.connectionStatus = 'disconnected';
                resolve(false);
            }
        });
    }

    /**
     * Start a heartbeat to keep the connection alive
     */
    private startHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }

        this.heartbeatInterval = setInterval(() => {
            if (this.connectionStatus === 'connected' && this.socket && this.socket.readyState === WebSocket.OPEN) {
                try {
                    // Send ping message to keep connection alive
                    this.socket.send(JSON.stringify({
                        type: 'ping'
                    }));
                } catch (error) {
                    console.warn('Error sending heartbeat ping:', error);
                }
            }
        }, this.heartbeatIntervalTime);
    }

    /**
     * Attempt to reconnect to WebSocket with exponential backoff
     */
    private attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            // After max attempts, reset count but increase wait time substantially
            this.reconnectAttempts = 0;

            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
            }

            // Try again after 1 minute
            this.reconnectTimeout = setTimeout(() => {
                this.connect();
            }, 60000);

            return;
        }

        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }

        this.reconnectAttempts++;

        // Exponential backoff with max of 30 seconds
        const delay = Math.min(this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1), 30000);

        this.reconnectTimeout = setTimeout(() => {
            this.connect();
        }, delay);
    }

    /**
     * Handle incoming WebSocket messages
     */
    private handleMessage(event: MessageEvent) {
        try {
            const message = JSON.parse(event.data);

            // Basic message validation
            if (!message) {
                console.warn('Empty message received');
                return;
            }

            // Handle pong responses from heartbeat pings
            if (message.type === 'pong') {
                // Connection is alive, nothing else needed
                return;
            }

            // Prioritize transaction and price data for wallet balances and charts
            if ((message.type === 'transaction' || message.type === 'price' ||
                (message.room && (message.room.startsWith('transaction:') || message.room.startsWith('price:')))) &&
                this.eventListeners.has(message.room)) {
                // Process high priority messages immediately
                const listeners = this.eventListeners.get(message.room);
                if (listeners) {
                    const messageData = message.data || {};
                    listeners.forEach(callback => {
                        try {
                            // We need immediate updates for price data to maintain chart continuity
                            if (message.room?.startsWith('price:')) {
                                // Call immediately without animation frame for time-critical price updates
                                callback(messageData);
                            } else {
                                // Use requestAnimationFrame for smoother UI updates with other data
                                requestAnimationFrame(() => callback(messageData));
                            }
                        } catch (error) {
                            console.error('Error in WebSocket high priority callback', error);
                        }
                    });
                }
                return;
            }

            // Handle server-initiated heartbeats or status messages (which may have empty data)
            if (message.type === 'ping' || message.type === 'status' || message.type === 'heartbeat') {
                // Just respond with a pong if needed
                if (this.socket && this.connectionStatus === 'connected' && this.socket.readyState === WebSocket.OPEN && message.type === 'ping') {
                    try {
                        this.socket.send(JSON.stringify({ type: 'pong' }));
                    } catch (error) {
                        console.warn('Error sending pong response:', error);
                    }
                }
                return;
            }

            // Check if this is an empty data message (initialization or heartbeat)
            const messageData = message.data || {};
            const isEmpty = Object.keys(messageData).length === 0 ||
                (Object.keys(messageData).length === 1 && 'time' in messageData);

            // Skip processing empty data messages to avoid validation warnings
            if (isEmpty && message.room) {
                return;
            }

            // Check for transaction data in different formats
            if (message.type === 'transaction' || (message.room && message.room.startsWith('transaction:'))) {


                // Special handling for transaction data which can come in various formats
                if (message.room && this.eventListeners.has(message.room)) {
                    const listeners = this.eventListeners.get(message.room);
                    if (listeners) {
                        // Handle array data format
                        if (Array.isArray(messageData)) {
                            if (messageData.length > 0) {
                                // Dispatch to all registered listeners for this room
                                listeners.forEach(callback => {
                                    try {
                                        callback(messageData);
                                    } catch (error) {
                                        console.error('Error in WebSocket callback for array data', error);
                                    }
                                });
                                return; // Handled array data, no need to proceed further
                            }
                        }

                        // If it's not an array or we should continue with normal processing...
                        // Check for minimum transaction fields depending on expected format
                        // The data could be in the messageData directly, or nested in a 'trades' field, or other formats
                        if (
                            messageData.tx || // Direct tx field
                            (messageData.trades && Array.isArray(messageData.trades) && messageData.trades.length > 0) || // trades array
                            (Array.isArray(messageData) && messageData.some(item => item && item.tx)) // Array with some valid items
                        ) {
                            listeners.forEach(callback => {
                                try {
                                    callback(messageData);
                                } catch (error) {
                                    console.error('Error in WebSocket callback', error);
                                }
                            });
                            return; // We've dispatched the message, no need for the standard handling below
                        } else {
                            // Try to extract whatever transaction data we can find
                            let extractedData = null;

                            // Check if there's any transaction-like data we can use
                            if (typeof messageData === 'object' && messageData !== null) {
                                // If it has any of the fields we expect in a transaction, use it
                                const hasTransactionFields = ['amount', 'price', 'volume', 'wallet', 'time'].some(
                                    field => field in messageData
                                );

                                if (hasTransactionFields) {
                                    extractedData = messageData;
                                }
                            }

                            if (extractedData) {
                                listeners.forEach(callback => {
                                    try {
                                        callback(extractedData);
                                    } catch (error) {
                                        console.error('Error in WebSocket callback for extracted data', error);
                                    }
                                });
                                return; // Handled the extracted data
                            }

                            console.warn('Incomplete transaction data, skipping callbacks', messageData);
                        }
                    }
                    return; // We've handled (or skipped) the transaction message
                }
            }

            // Standard message handling for non-transaction messages
            // Dispatch to registered event listeners
            if (message.room && this.eventListeners.has(message.room)) {
                const listeners = this.eventListeners.get(message.room);
                if (listeners) {
                    // Validate the message data based on room type
                    if (message.room.startsWith('price:')) {
                        // Price updates should have a price field (handle both nested and flat structures)
                        const hasValidPrice = (messageData.price && typeof messageData.price === 'number') ||
                            (messageData.price?.usd && typeof messageData.price.usd === 'number');
                        if (!hasValidPrice) {
                            console.warn('Invalid price data, skipping callbacks', messageData);
                            return; // Skip callbacks for invalid price data
                        }
                    } else if (message.room.startsWith('stats:')) {
                        // Stats updates (handle both nested and flat structures)
                        const hasValidStats = messageData.marketCap || messageData.marketCap?.usd ||
                            messageData.volume24h || messageData.holders ||
                            messageData.curvePercentage !== undefined;
                        if (!hasValidStats) {
                            console.warn('Incomplete stats data, skipping callbacks', messageData);
                            // Don't call callbacks for incomplete stats data
                            return;
                        }
                    }

                    // Ensure time exists if missing
                    if (!messageData.time) {
                        messageData.time = Date.now();
                    }

                    listeners.forEach(callback => {
                        try {
                            callback(messageData);
                        } catch (error) {
                            // Error in callback
                            console.error('Error in WebSocket callback', error);
                        }
                    });
                }
            } else if (message.room) {
                console.warn(`No listeners found for room: ${message.room}`);
            }

            // Also emit a general message to all listeners
            if (this.eventListeners.has('message')) {
                const listeners = this.eventListeners.get('message');
                if (listeners) {
                    listeners.forEach(callback => {
                        try {
                            callback(message);
                        } catch (error) {
                            // Error in callback
                            console.error('Error in general message callback', error);
                        }
                    });
                }
            }
        } catch (error) {
            // Error parsing message
            console.error('Error parsing WebSocket message:', error);
        }
    }

    /**
     * Join a room to receive specific events
     * @param roomId Room identifier (e.g., 'transaction:{tokenAddress}', 'price:{tokenAddress}', etc.)
     */
    joinRoom(roomId: string) {
        if (!roomId) {
            return;
        }

        // Store this room in our set of joined rooms
        this.joinedRooms.add(roomId);

        if (this.connectionStatus !== 'connected') {
            this.connect().then((connected) => {
                if (connected) {
                    this.sendJoinRoomMessage(roomId);
                }
            });
            return;
        }

        this.sendJoinRoomMessage(roomId);
    }

    /**
     * Send a join room message to the server
     */
    private sendJoinRoomMessage(roomId: string) {
        if (!this.socket || this.connectionStatus !== 'connected') {
            console.warn(`Cannot join room ${roomId}: WebSocket not connected`);
            return;
        }

        // Additional check for WebSocket readyState to prevent "Still in CONNECTING state" errors
        if (this.socket.readyState !== WebSocket.OPEN) {
            console.warn(`Cannot join room ${roomId}: WebSocket not ready (readyState: ${this.socket.readyState})`);
            // Queue the room join for when the connection is fully ready
            setTimeout(() => {
                if (this.socket && this.socket.readyState === WebSocket.OPEN && this.connectionStatus === 'connected') {
                    this.sendJoinRoomMessage(roomId);
                }
            }, 100);
            return;
        }

        try {
            const joinMessage = JSON.stringify({
                type: 'join',
                room: roomId,
                // Include API key in the join message if available
                ...(this.apiKey ? { apiKey: this.apiKey } : {})
            });

            console.log(`📡 Joining room: ${roomId}`);
            console.log(`📤 Sending join message:`, joinMessage);
            this.socket.send(joinMessage);
        } catch (error) {
            console.error(`Error joining room ${roomId}:`, error);
        }
    }

    /**
     * Leave a room to stop receiving events
     * @param roomId Room identifier
     */
    leaveRoom(roomId: string) {
        if (!roomId || !this.socket || this.connectionStatus !== 'connected') {
            return;
        }

        this.joinedRooms.delete(roomId);

        // Check WebSocket readyState before sending
        if (this.socket.readyState !== WebSocket.OPEN) {
            return;
        }

        try {
            this.socket.send(JSON.stringify({
                type: 'leave',
                room: roomId
            }));
        } catch (error) {
            // Error leaving room
        }
    }

    /**
     * Subscribe to price updates for a token
     * @param tokenAddress Token mint address
     * @param callback Function to call when price updates are received
     * @returns Function to unsubscribe
     */
    subscribeToPrice(tokenAddress: string, callback: (data: any) => void) {
        const roomId = `price:${tokenAddress}`;
        this.joinRoom(roomId);
        return this.on(roomId, callback);
    }

    /**
     * Subscribe to transaction updates for a token
     * @param tokenAddress Token mint address
     * @param callback Function to call when transaction updates are received
     * @returns Function to unsubscribe
     */
    subscribeToTransactions(tokenAddress: string, callback: (data: any) => void) {
        // Validate the token address isn't empty
        if (!tokenAddress) {
            console.warn('Empty token address provided to subscribeToTransactions');
            return () => { };
        }

        // Clean the token address of any whitespace
        const cleanedAddress = tokenAddress.trim();

        // Create the room ID for transactions
        const roomId = `transaction:${cleanedAddress}`;

        // Join the room
        this.joinRoom(roomId);

        // Create a wrapper callback that handles different transaction formats
        const transactionHandler = (data: any) => {

            // Solana Tracker API may send transaction data in various formats:
            // 1. A single transaction object with tx field
            // 2. An array of transaction objects
            // 3. An object with a 'transactions' array
            // 4. An object with partial transaction data (amount, trader, time)

            if (Array.isArray(data)) {
                // If it's an array, validate and filter it
                const validTransactions = data.filter(tx => {
                    // Consider it valid if it has any transaction-like fields
                    return tx && (tx.tx || tx.wallet || tx.amount || tx.volume || tx.time);
                });

                if (validTransactions.length > 0) {
                    // Process each transaction in array
                    callback(validTransactions);
                } else {
                    console.warn('Received empty or invalid transaction array:', data);
                }
            } else if (data && data.transactions && Array.isArray(data.transactions)) {
                // If it has a transactions array property
                const validTransactions = data.transactions.filter((tx: any) => {
                    return tx && (tx.tx || tx.wallet || tx.amount || tx.volume || tx.time);
                });

                if (validTransactions.length > 0) {
                    callback(validTransactions);
                } else {
                    console.warn('Received empty or invalid transactions array property:', data.transactions);
                }
            } else if (data && (data.tx || data.wallet || data.amount || data.volume || data.time)) {
                // If it's a single transaction object with at least one valid field
                // Create a normalized transaction object
                const normalizedTx = {
                    tx: data.tx || `unknown-${Date.now()}`, // Generate a temporary ID if missing
                    amount: data.amount || 0,
                    priceUsd: data.priceUsd || data.price || 0,
                    volume: data.volume || 0,
                    type: data.type || 'buy',
                    wallet: data.wallet || 'unknown',
                    time: data.time || Date.now(),
                    program: data.program || 'unknown'
                };

                callback(normalizedTx);
            } else {
                console.warn('Received transaction data in unexpected format:', data);
            }
        };

        // Register the callback with the wrapper
        return this.on(roomId, transactionHandler);
    }

    /**
     * Subscribe to transaction updates for a specific token and pool
     * @param tokenAddress Token mint address
     * @param poolId Pool ID (liquidity pool address)
     * @param callback Function to call when transaction updates are received
     * @returns Function to unsubscribe
     */
    subscribeToPoolTransactions(tokenAddress: string, poolId: string, callback: (data: any) => void) {
        const roomId = `transaction:${tokenAddress}:${poolId}`;
        this.joinRoom(roomId);
        return this.on(roomId, callback);
    }

    /**
     * Subscribe to wallet-specific transactions for a token
     * @param tokenAddress Token mint address
     * @param walletAddress Wallet address to monitor
     * @param callback Function to call when transaction updates are received
     * @returns Function to unsubscribe
     */
    subscribeToWalletTransactions(tokenAddress: string, walletAddress: string, callback: (data: any) => void) {
        const roomId = `transaction:${tokenAddress}:wallet:${walletAddress}`;
        this.joinRoom(roomId);
        return this.on(roomId, callback);
    }

    /**
     * Subscribe to stats updates for a token
     * @param tokenAddress Token mint address
     * @param callback Function to call when stats updates are received
     * @returns Function to unsubscribe
     */
    subscribeToStats(tokenAddress: string, callback: (data: any) => void) {
        const roomId = `stats:${tokenAddress}`;
        this.joinRoom(roomId);
        return this.on(roomId, callback);
    }

    /**
     * Register an event listener for a specific room
     * @param roomId Room identifier (e.g., 'transaction:{tokenAddress}') or 'message' for all messages
     * @param callback Function to call when event is received
     * @returns Function to remove the event listener
     */
    on(roomId: string, callback: (data: any) => void) {
        if (!this.eventListeners.has(roomId)) {
            this.eventListeners.set(roomId, new Set());
        }

        const listeners = this.eventListeners.get(roomId);
        if (listeners) {
            listeners.add(callback);
        }

        return () => {
            const listeners = this.eventListeners.get(roomId);
            if (listeners) {
                listeners.delete(callback);
            }
        };
    }

    /**
     * Disconnect WebSocket
     */
    disconnect() {
        if (this.socket && this.connectionStatus === 'connected') {
            try {
                this.socket.close();
            } catch (error) {
                console.error('Error closing WebSocket connection:', error);
            }
        }

        this.connectionStatus = 'disconnected';
        this.socket = null;
        this.joinedRooms.clear(); // Clear all joined rooms
        this.resetReconnectAttempts(); // Reset the reconnect counter

        // Clear all timers
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * Reset reconnect attempts counter
     * This is useful when the service is intentionally disconnected
     * and should not auto-reconnect until explicitly requested
     */
    resetReconnectAttempts() {
        this.reconnectAttempts = 0;
    }

    /**
     * Make sure connection is active, reconnect if needed
     */
    ensureConnection() {
        if (this.connectionStatus !== 'connected') {
            return this.connect();
        }
        return Promise.resolve(true);
    }

    /**
     * Get the current connection status
     * @returns The current connection status: 'connected', 'connecting', or 'disconnected'
     */
    getConnectionStatus(): 'connected' | 'connecting' | 'disconnected' {
        return this.connectionStatus;
    }

    /**
     * Check if WebSocket is currently connected
     * @returns True if connected, false otherwise
     */
    isConnected(): boolean {
        return this.connectionStatus === 'connected' && this.socket !== null;
    }

    /**
     * Send a message through the WebSocket
     * @param type Message type
     * @param data Message data
     * @returns True if sent successfully, false otherwise
     */
    send(type: string, data: any): boolean {
        if (!this.isConnected() || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
            console.error('Cannot send message: WebSocket not connected or not ready');
            return false;
        }

        try {
            this.socket.send(JSON.stringify({
                type,
                data
            }));
            return true;
        } catch (error) {
            console.error('Error sending WebSocket message:', error);
            return false;
        }
    }
}

// Export a singleton instance
export const wsService = new WebSocketService();

// Do NOT initialize the connection when this module is imported
// Let components initialize it when needed

export default wsService; 